import { useRef, useState } from 'react'
import Icon from '../Icon'
import { addPhotos, photoUrl, removePhoto, setCover, uploadPhoto, type Photo } from '../../lib/ppc'

/**
 * Photographs of one plant.
 *
 * ── Append, never replace ─────────────────────────────────────────────────────
 *
 * The old form kept one photo: taking a second replaced the first. Every path
 * here adds to what is already there, and removing is a separate, deliberate
 * action on a named photo.
 *
 * ── Two buttons, because they are two different acts ──────────────────────────
 *
 * `capture="environment"` opens the camera directly. Without it the same input
 * offers a file picker, which is what Dillon wants when the photo is already on
 * the phone. One input with both behaviours cannot be had, so there are two.
 */
export default function PhotoPicker({
  plantId, photos, onChange, pending, onPending, openCamera, openLibrary,
}: {
  /** Null while the plant is still being created — photos are held until save. */
  plantId: string | null
  photos: Photo[]
  onChange: (photos: Photo[]) => void
  /** Upload ids taken before the plant exists. */
  pending: string[]
  onPending: (ids: string[]) => void
  /** Filled in so the add-chooser can open a picker directly. */
  openCamera?: { current: (() => void) | null }
  openLibrary?: { current: (() => void) | null }
}) {
  const camera = useRef<HTMLInputElement>(null)
  const library = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (openCamera) openCamera.current = () => camera.current?.click()
  if (openLibrary) openLibrary.current = () => library.current?.click()

  async function take(files: FileList | null) {
    if (!files?.length) return
    setBusy(true)
    setError(null)
    try {
      const ids: string[] = []
      for (const file of Array.from(files)) ids.push(await uploadPhoto(file))
      if (plantId) onChange(await addPhotos(plantId, ids))
      else onPending([...pending, ...ids])
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That photo would not upload.')
    } finally {
      setBusy(false)
      // Cleared so choosing the SAME file twice still fires a change event.
      if (camera.current) camera.current.value = ''
      if (library.current) library.current.value = ''
    }
  }

  const shown = plantId ? photos : pending.map((id) => ({
    id, upload_id: id, is_cover: 0, sort_order: 0, object_key: '', content_type: '',
  } as Photo))

  return (
    <div className="photo-picker">
      <div className="row">
        <button type="button" onClick={() => camera.current?.click()} disabled={busy}>
          <Icon name="camera" size={15} /> Take a photo
        </button>
        <button type="button" onClick={() => library.current?.click()} disabled={busy}>
          <Icon name="plus" size={15} /> Choose photos
        </button>
        {busy && <span className="muted small">Uploading…</span>}
      </div>

      <input
        ref={camera} type="file" accept="image/*" capture="environment" multiple hidden
        onChange={(event) => void take(event.target.files)}
      />
      <input
        ref={library} type="file" accept="image/*" multiple hidden
        onChange={(event) => void take(event.target.files)}
      />

      {error && <p className="error small">{error}</p>}

      {shown.length > 0 && (
        <div className="photo-grid">
          {shown.map((photo) => (
            <figure key={photo.id} className={photo.is_cover ? 'cover' : undefined}>
              <img src={photoUrl(photo)} alt="" loading="lazy" />
              <figcaption>
                {plantId && !photo.is_cover && (
                  <button
                    type="button" className="link small"
                    onClick={async () => onChange(await setCover(plantId, photo.id))}
                  >
                    Make cover
                  </button>
                )}
                {photo.is_cover ? <span className="chip">Cover</span> : null}
                <button
                  type="button" className="link small danger"
                  onClick={async () => {
                    if (plantId) onChange(await removePhoto(plantId, photo.id))
                    else onPending(pending.filter((id) => id !== photo.id))
                  }}
                >
                  Remove
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {shown.length === 0 && <p className="muted small">No photos yet.</p>}
    </div>
  )
}
